// src/review/utils/dataTransformers.js

/**
 * Transform hierarchical risk data into flat list for review
 * Dynamically handles any number of areas and risks - NO HARD-CODING
 *
 * @param {Object} riskFormData - Risk form data from Redux
 * @returns {Array} Flat list of all saved risks with area and source info
 */
export const transformRisksToFlatList = (riskFormData) => {
  if (!riskFormData || typeof riskFormData !== 'object') {
    return [];
  }

  const allRisks = [];

  // Dynamically iterate through all areas (not hard-coded)
  Object.keys(riskFormData).forEach((areaName) => {
    const areaData = riskFormData[areaName];
    const areaRisks = areaData?.areaRisks || {};

    // Dynamically iterate through all sources in this area
    Object.keys(areaRisks).forEach((sourceName) => {
      const risks = areaRisks[sourceName];

      if (Array.isArray(risks)) {
        risks.forEach((risk) => {
          // Only include saved risks in review
          if (risk.status === 'Saved') {
            allRisks.push({
              ...risk,
              areaName,
              sourceName,
              // Preserve all original fields
            });
          }
        });
      }
    });
  });

  return allRisks;
};

/**
 * Group risks by area for navigator
 * Dynamically creates groups based on actual data
 *
 * @param {Array} risks - Flat list of risks
 * @returns {Object} Risks grouped by area name
 */
export const groupRisksByArea = (risks) => {
  if (!Array.isArray(risks)) {
    return {};
  }

  return risks.reduce((groups, risk) => {
    const areaName = risk.areaName;
    if (!groups[areaName]) {
      groups[areaName] = [];
    }
    groups[areaName].push(risk);
    return groups;
  }, {});
};

/**
 * Transform security threats into flat list for review
 * Maps scenario data from Redux to threat objects with consistent field names
 *
 * @param {Object} securityState - Security state from Redux (state.security.models[modelId])
 * @returns {Array} Flat list of all security threat scenarios
 */
export const transformThreatsToFlatList = (securityState) => {
  if (!securityState || typeof securityState !== 'object') {
    return [];
  }

  const allThreats = [];
  const scenarios = securityState.scenarios || {};

  // Dynamically iterate through all scenarios
  Object.keys(scenarios).forEach((scenarioId) => {
    const scenario = scenarios[scenarioId];

    if (scenario) {
      // Map scenario fields to threat object with consistent naming
      allThreats.push({
        ...scenario, // Preserve all original fields
        threatId: scenarioId, // Use scenarioId as threatId
        scenarioId: scenarioId, // Also keep scenarioId
        threatScenario: scenario.name || scenarioId, // Map name to threatScenario for display
        threatName: scenario.name || scenarioId, // Also keep threatName for compatibility
        category: scenario.category || 'Uncategorized',
        threatActor: scenario.mappedAgents?.join(', ') || 'Unknown',
        attackVector: scenario.attackSurface?.description || scenario.attributes?.description || null,
        // Impact is already on the scenario object
        // assessmentData is already on the scenario object
        // Controls are fetched separately from securityControls[scenarioId]
      });
    }
  });

  return allThreats;
};

/**
 * Group threats by category for navigator
 * Dynamically creates groups based on actual data
 *
 * @param {Array} threats - Flat list of threats
 * @returns {Object} Threats grouped by category
 */
export const groupThreatsByCategory = (threats) => {
  if (!Array.isArray(threats)) {
    return {};
  }

  return threats.reduce((groups, threat) => {
    const category = threat.category || 'Uncategorized';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(threat);
    return groups;
  }, {});
};

/**
 * Calculate review progress
 *
 * @param {Array} items - List of items (risks or threats)
 * @param {Array} reviewedIds - List of reviewed item IDs
 * @returns {Object} Progress information
 */
export const calculateProgress = (items, reviewedIds) => {
  const total = items?.length || 0;
  const reviewed = reviewedIds?.length || 0;
  const percentage = total > 0 ? Math.round((reviewed / total) * 100) : 0;

  return {
    total,
    reviewed,
    remaining: total - reviewed,
    percentage
  };
};
